import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Card, 
  Button, 
  Space, 
  Typography, 
  Row, 
  Col,
  notification,
  Spin,
  Divider
} from 'antd';
import { 
  InfoCircleOutlined
} from '@ant-design/icons';
import { httpService } from '../../services/httpService';
import { useTheme } from '../../theme/ThemeProvider';

import TripCard from '../../components/TripCard';
import './styles.css';

const { Title, Text } = Typography;

interface TripOption {
  id: string;
  carType: string;
  carTypePersian: string;
  price: number;
  carIcon: string;
  backgroundColor: string;
}







// Helper function to format price with comma separators
const formatPrice = (price: number): string => {
  return Math.floor(price).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const TripOptions: React.FC = () => {
  
  const location = useLocation();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [tripOptions, setTripOptions] = useState<TripOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [tripDetails, setTripDetails] = useState<any>(null);
  const [selectedOption, setSelectedOption] = useState<TripOption | null>(null);
  const [reserving, setReserving] = useState(false);
  const [hasFetchedOptions, setHasFetchedOptions] = useState(false);
  
  // Extract tripId from URL params or location state
  const tripId = new URLSearchParams(location.search).get('tripId') || location.state?.tripId;

  // Function to fetch trip details from API
  const fetchTripDetails = async (id: string) => {
    try {
      const response = await httpService.getTripDetails(id);
      
      // Check if response has data property or if response itself is the data
      if (response && response.data) {
        setTripDetails(response.data);
      } else if (response) {
        setTripDetails(response);
      } else {
        notification.warning({ message: 'اطلاعات سفر یافت نشد' });
      }
    } catch (error) {
      console.error('❌ Error fetching trip details:', error);
      notification.error({ message: 'خطا در دریافت اطلاعات سفر' });
    }
  };

  // Single useEffect to handle all logic
  useEffect(() => {
    console.log('🔍 useEffect triggered - tripId:', tripId, 'location.state:', !!location.state?.tripDetails, 'tripDetails:', !!tripDetails, 'hasFetchedOptions:', hasFetchedOptions);
    
    if (!tripId) {
      // No tripId - redirect to home
      notification.error({ message: 'شناسه سفر یافت نشد' });
      navigate('/home');
      return;
    }

    // If we have tripDetails from location.state, set them first
    if (location.state?.tripDetails && !tripDetails) {
      console.log('📋 Setting trip details from location.state');
      setTripDetails(location.state.tripDetails);
      setLoading(false);
      return; // Don't fetch anything yet, wait for next render
    }

    // Now handle fetching logic
    if (tripId && !location.state?.tripDetails) {
      // No tripDetails from state, fetch from API
      console.log('📡 Fetching both details and options from API');
      fetchTripDetails(tripId);
      fetchTripOptions(tripId);
    } else if (tripId && tripDetails && !hasFetchedOptions) {
      // We have tripDetails (either from state or API), fetch options once
      console.log('📡 Fetching only options (details already available)');
      fetchTripOptions(tripId);
    } else {
      console.log('🚫 No fetch needed - all data available');
    }
  }, [tripId, tripDetails, hasFetchedOptions]); // Include all necessary dependencies


  const fetchTripOptions = async (id: string) => {
    try {
      setLoading(true);
      const response = await httpService.getTripOptions(id);
      
      // Parse the actual API response
      if (response && response.items && Array.isArray(response.items)) {
        const options: TripOption[] = response.items.map((item: any, index: number) => {
          // Determine car icon based on group
          let carIcon = '/dist/car_silver.svg'; // default
          if (item.group === 1) {
            carIcon = '/dist/car_silver.svg';
          } else if (item.group === 2) {
            carIcon = '/dist/car_gold.svg';
          } else if (item.group === 3) {
            carIcon = '/dist/car_diamond.svg';
          }

          return {
            id: item.group?.toString() || index.toString(), // Use actual group from API
            carType: `Group ${item.group}`,
            carTypePersian: item.groupDesription,
            price: item.unitPrice,
            carIcon: carIcon,
            backgroundColor: '#ffffff'
          };
        });
        
        // Sort options by price from lowest to highest
        const sortedOptions = options.sort((a, b) => a.price - b.price);
        
        setTripOptions(sortedOptions);
        setHasFetchedOptions(true); // Mark as fetched
      } else {
        // Fallback to empty array if response is invalid
        setTripOptions([]);
        setHasFetchedOptions(true); // Mark as fetched even if empty
      }
    } catch (error) {
      console.error('❌ Error fetching trip options:', error);
      notification.error({ message: 'خطا در دریافت گزینه های سفر' });
      setTripOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOptionSelect = (option: TripOption) => {
    setSelectedOption(option);
    notification.success({ 
      message: `${option.carTypePersian} انتخاب شد`,
      description: `قیمت: ${option.price} تومان`
    });
  };

  const handleBackToHome = () => {
    navigate('/home');
  };

  const handleReservation = async () => {
    console.log('🚀 handleReservation called');
    console.log('🔍 selectedOption:', selectedOption);
    console.log('🔍 tripId:', tripId);
    console.log('🔍 tripDetails:', tripDetails);
    
    if (!selectedOption) {
      console.log('⚠️ No option selected');
      notification.warning({ message: 'لطفاً ابتدا یک نوع خودرو انتخاب کنید' });
      return;
    }

    try {
      setReserving(true);
      
      // Prepare the data for SetTripOption API
      const requestData = {
        id: tripId, // Use the actual tripId from the component
        group: parseInt(selectedOption.id), // Use the selected option's group
        selectedTripOption: {
          code: parseInt(selectedOption.id),
          unitPrice: selectedOption.price,
          title: selectedOption.carTypePersian,
          tripItemType: 1, // Default to Distance type
          info: `انتخاب شده: ${selectedOption.carTypePersian}`,
          group: parseInt(selectedOption.id)
        }
      };
      
      console.log('📤 Request Data:', requestData);
      
      const response = await httpService.setTripOption(requestData);
      
      notification.success({ 
        message: 'رزرو با موفقیت انجام شد',
        description: `${selectedOption.carTypePersian} برای شما رزرو شد`
      });
      
      // Extract the ID from response if available, otherwise use the original tripId
      const finalTripId = response?.id || response?.tripId || tripId;
      
      // Navigate to trip page with the final trip ID using URL parameter
      navigate(`/trip/${finalTripId}`, { 
        state: { 
          originalTripId: tripId,
          tripDetails: tripDetails,
          selectedOption: selectedOption
        }
      });
      
    } catch (error) {
      console.error('❌ Error setting trip option:', error);
      notification.error({ message: 'خطا در انجام رزرو' });
    } finally {
      setReserving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column'
      }}>
        <Spin size="large" />
        <Text style={{ marginTop: 16 }}>در حال دریافت گزینه های سفر...</Text>
      </div>
    );
  }

  return (
    <div className="trip-options-container" >


      {/* Trip Details */}
      {tripDetails && (
        <div className="trip-details-section">
          <TripCard 
            trip={{
              id: tripDetails.id || 'temp-id',
              type: tripDetails.type || 1,
              startDate: tripDetails.startDate || '',
              startTime: tripDetails.startTime || '',
              endDate: tripDetails.endDate || '',
              endTime: tripDetails.endTime || '',
              durationMinutes: 0,
              source: {
                id: tripDetails.source?.id || 'temp-source',
                address: tripDetails.source?.address || '3342 Hill Street, Jacksonville, FL 32202'
              },
              destination: {
                id: tripDetails.destination?.id || 'temp-dest',
                address: tripDetails.destination?.address || '3342 Hill Street, Jacksonville, FL 32202'
              },
              state: 0,
              createdAt: tripDetails.createdAt || ''
            }}
            className="trip-details-trip-card"
          />
        </div>
      )}

      <Divider />

      {/* Trip Options */}
      <div className="trip-options-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <Title level={4} style={{ margin: 0, color: theme.text.primary }}>
            انتخاب گروه خودرویی
          </Title>
        </div>
        
        {tripOptions.length > 0 ? (
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            {tripOptions.map((option) => (
            <Card 
              key={option.id}
              className="trip-option-card"
              bodyStyle={{
                backgroundColor: selectedOption?.id === option.id ? '#f0f0f0' : option.backgroundColor
              }}
              style={{ 
                backgroundColor: selectedOption?.id === option.id ? '#f0f0f0' : option.backgroundColor,
                border: selectedOption?.id === option.id ? `2px solid ${theme.primary}` : `1px solid ${theme.border}`,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
             
              }}
              onClick={() => handleOptionSelect(option)}
            >
              <Row align="middle" justify="space-between" style={{ padding: '0px 8px' }}  >
                <Col span={4}>
                  <div className="car-icon">
                    <img 
                      src={option.carIcon}
                      alt={option.carTypePersian}
                      style={{ 
                        width: '72px', 
                        height: '72px',
                        
                        objectFit: 'contain'
                      }}
                    />
                  </div>
                </Col>
                
                <Col span={12}>
                  <div className="car-info">
                    <div className="car-type">
                      <Text strong>{option.carTypePersian}</Text>
                      <InfoCircleOutlined 
                        className="info-icon" 
                        style={{ marginRight: 8, color: '#666' }}
                      />
                    </div>

                  </div>
                </Col>
                
                <Col span={8} style={{ textAlign: 'left' }}>
                  <div className="price-section">
                    <Text className="price-amount">
                      {formatPrice(option.price)} تومان
                    </Text>
                  </div>
                </Col>
              </Row>
            </Card>
          ))}
          </Space>
        ) : (
          <div style={{ 
            textAlign: 'center', 
            padding: '8px 8px',
            backgroundColor: '#f5f5f5',
            borderRadius: '8px',
            marginBottom: '8px'
          }}>
            <InfoCircleOutlined style={{ fontSize: 48, color: '#999', marginBottom: 16 }} />
            <Text style={{ fontSize: 16, color: '#666' }}>
              در حال حاضر گزینه‌های خودرو برای این سفر در دسترس نیست
            </Text>
            <br />
            <Text style={{ fontSize: 14, color: '#999' }}>
              لطفاً بعداً تلاش کنید یا با پشتیبانی تماس بگیرید
            </Text>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="action-buttons" style={{ padding: '8px' }}>
        <Button 
          type="default" 
          size="large" 
          onClick={handleBackToHome}
          style={{ 
            marginBottom: 8,
            height: 48,
            width: '100%',
            fontSize: 16
          }}
        >
          بازگشت
        </Button>
        
        <Button 
          type="primary" 
          size="large" 
          onClick={handleReservation}
          loading={reserving}
          disabled={!selectedOption}
          style={{ 
            height: 48, 
            fontSize: 16, 
            width: '100%'
          }}
        >
          {reserving ? 'در حال رزرو...' : 'درخواست رزرو'}
        </Button>
      </div>
    </div>
  );
};

export default TripOptions;

